// src/baseFetch.js
import fetch from "isomorphic-fetch";
var DEFAULT_TIMEOUT = 5e3;
var RESPONSE_TYPES = {
  TEXT: "text",
  JSON: "json"
};
var windowProtocol;
var isValidTimeout = (timeout) => {
  return timeout > 0;
};
var identity = (value) => {
  return value;
};
var tryParseResponseBody = (res) => {
  let contentType = RESPONSE_TYPES.TEXT;
  if (res && res.headers && res.headers.get) {
    const contentTypeStr = res.headers.get("content-type");
    contentType = contentTypeStr && contentTypeStr.toLowerCase().indexOf("json") > -1 ? RESPONSE_TYPES.JSON : contentType;
  }
  return res[contentType] ? res[contentType]() : Promise.resolve("An error occurred in baseFetch tryParseResponseBody");
};
var createErrorIfBadStatusCode = (res) => {
  if (res && res.status >= 400) {
    return tryParseResponseBody(res).then((body) => {
      const err = new Error(
        `Request to ${res.url} failed with status ${res.status}`
      );
      err.status = res.status;
      err.statusText = res.statusText;
      err.response = body;
      return err;
    });
  }
  return res;
};
var throwIfError = (res) => {
  if (res && res.constructor === Error) {
    throw res;
  }
  return res;
};
var baseFetch = (baseUrl, options = {}) => {
  if (!options.caller) {
    throw new Error('Option "caller" MUST be supplied to fetch!');
  }
  const ptr = {};
  const promise = new Promise((resolve, reject) => {
    ptr.resolve = resolve;
    ptr.reject = reject;
  });
  const handleResponse = options.dontThrowOnBadStatus ? identity : createErrorIfBadStatusCode;
  options.headers = options.headers || {};
  options.headers["x-caller-app"] = options.caller;
  const optionsToUse = {
    ...options,
    timeout: isValidTimeout(options.timeout) && +options.timeout || DEFAULT_TIMEOUT
  };
  if (!baseUrl) {
    return Promise.reject(new Error("Cannot fetch empty url"));
  }
  if (typeof baseUrl !== "string") {
    return Promise.reject(new Error("Unable to fetch non-string url"));
  }
  if (options.timeout && typeof window !== "undefined") {
    ptr.timeout = setTimeout(
      () => ptr.reject(new Error("Request timed out")),
      options.timeout
    );
  }
  if (baseUrl.substring(0, 4).toLowerCase() === "http") {
    fetch(baseUrl, optionsToUse).then((v) => {
      ptr.timeout && clearTimeout(ptr.timeout);
      return v;
    }).then(handleResponse).then(throwIfError).then(ptr.resolve).catch(ptr.reject);
    return promise;
  }
  windowProtocol = windowProtocol || typeof window !== "undefined" && window.location && window.location.protocol;
  const protocol = windowProtocol || "http:";
  let url;
  if (baseUrl.indexOf("//") === 0) {
    url = `${protocol}${baseUrl}`;
  } else if (baseUrl.indexOf("/") === 0) {
    url = baseUrl;
  } else {
    url = `${protocol}//${baseUrl}`;
  }
  fetch(url, optionsToUse).then((v) => {
    ptr.timeout && clearTimeout(ptr.timeout);
    return v;
  }).then(handleResponse).then(throwIfError).then(ptr.resolve).catch(ptr.reject);
  return promise;
};

// src/jsonFetch.js
var jsonFetch = (url, options) => {
  options.headers = options.headers || {};
  options.headers.Accept = options.headers.Accept || "application/json";
  return baseFetch(url, options).then(
    (res) => res.status === 204 ? {} : res.json()
  );
};

// src/jsonFetchKeepAlive.js
import http from "http";
var keepAliveAgent = new http.Agent({
  keepAlive: true
});
var jsonFetchKeepAlive = (url, options) => {
  return jsonFetch(url, { ...options, agent: keepAliveAgent });
};

// src/textFetch.js
var textFetch = (url, options) => {
  return baseFetch(url, options).then((res) => res.text());
};

// src/tryCatch.js
var tryCatch = async (f) => {
  let err;
  let res;
  try {
    res = await f();
  } catch (_err) {
    err = _err;
  }
  return [err, res];
};

// src/probe.js
import { hrtime } from "process";
import fetch3 from "isomorphic-fetch";

// src/cache.js
import fetch2 from "isomorphic-fetch";
var oneHour = 60 * 60 * 1e3;
if (!process) {
  throw new Error("\u{1F468}\u200D\u2708\uFE0F Cache stays in backend");
}
var noop = () => {
};
function createCache(options = {
  onCacheMiss: noop,
  onCacheSet: noop,
  onCacheRemove: noop,
  onCacheFlush: noop,
  name: "<not-set>"
}) {
  return {
    cache: {},
    refetchMap: {},
    repeatMap: {},
    /**
     *
     * @param {string} name
     * @returns any
     */
    get(name) {
      if (!this.cache[name]) {
        options.onCacheMiss(name);
      }
      return this.cache[name];
    },
    /**
     *
     * @param {Object} options
     * @param {string} options.name - name of the cache
     * @param {any} options.data - data to cache
     * @param {string} [options.url] - url for refetching
     * @param {number} options.ttl - time to live in milliseconds
     */
    set({ name, data, url, ttl }) {
      if (url) {
        this.refetchMap[name] = url;
      }
      if (!name || !data) {
        throw new Error("name and data are required");
      }
      this.cache[name] = data;
      if (ttl && Number.isInteger(ttl)) {
        setTimeout(() => {
          this.remove(name);
        }, ttl);
      }
      options.onCacheSet(name);
    },
    /**
     * @typedef {Object} options
     * @property {string} name
     * @property {string} url
     * @property {boolean} [refetchCache]
     * @property {number} [timeoutTime]
     * @property {number} [refetchTime]
     * @property {(data: any) => boolean} assertResponseIsInvalid
     * @property {number} ttl
     */
    async create({
      name,
      url,
      refetchCache = false,
      timeoutTime = 5e3,
      refetchTime = oneHour / 2,
      assertResponseIsInvalid,
      ttl
    }) {
      return new Promise((resolve, reject) => {
        const performCreate = () => {
          if (!name) {
            reject(new Error("name is required"));
          }
          if (this.cache[name] && !refetchCache) {
            resolve(this.cache[name]);
          }
          this.refetchMap[name] = url;
          fetch2(url).then(({ error, status, ...data }) => {
            if (error || status >= 300) {
              throw new Error(error != null ? error : "Unsuccesful cache fetch");
            }
            if (data) {
              if (refetchCache) {
                this.repeatMap[name] = setTimeout(async () => {
                  performCreate();
                }, refetchTime);
              }
              if (assertResponseIsInvalid && assertResponseIsInvalid(data) === true) {
                throw new Error("fetch assertion triggered");
              } else {
                this.set({ name, data, url, ttl });
              }
              resolve(this.cache[name]);
            }
          }).catch((error) => {
            if (refetchCache) {
              setTimeout(() => {
                performCreate();
              }, timeoutTime);
            } else {
              reject(error);
            }
          });
        };
        performCreate();
      });
    },
    /**
     *
     * @param {string} name
     */
    remove(name) {
      delete this.cache[name];
      options.onCacheRemove(name);
    },
    flush() {
      this.cache = {};
      options.onCacheFlush();
    }
  };
}

// src/probe.js
var cache = createCache();
var WARNING_DURATION_THRESHOLD_MS = 1e3;
var NANO_SECONDS_IN_MILLISECOND = 1000000n;
var probeStatus = {
  Ok: "Ok",
  Warning: "Warning",
  Error: "Error"
};
var getDuration = (start, end) => Number((end.valueOf() - start.valueOf()) / NANO_SECONDS_IN_MILLISECOND);
var dependencyHealthCheck = async ({ name, url, headers }) => {
  var _a;
  const start = hrtime.bigint();
  try {
    const res = await fetch3(url, { headers });
    const end = hrtime.bigint();
    const duration = getDuration(start, end);
    if (!res.ok) {
      const contentType = res.headers.get("Content-Type") || "";
      const isJsonResponse = /application\/json/.test(contentType);
      let data = {};
      if (isJsonResponse) {
        data = await res.json().catch(() => ({}));
      } else {
        data.text = await res.text().catch(() => "");
      }
      const status2 = probeStatus.Error;
      const message2 = `Status: ${res.status}, message: ${res.statusText}`;
      const dependency = {
        name,
        status: status2,
        duration,
        message: message2,
        data
      };
      return dependency;
    }
    const isVerySlow = duration >= WARNING_DURATION_THRESHOLD_MS;
    const status = isVerySlow ? probeStatus.Warning : probeStatus.Ok;
    const message = isVerySlow ? "response time slow" : "response time ok";
    return {
      name,
      status,
      duration,
      message,
      data: {}
    };
  } catch (err) {
    const end = hrtime.bigint();
    const duration = getDuration(start, end);
    const status = probeStatus.Error;
    const message = (_a = err == null ? void 0 : err.message) != null ? _a : "Probe check failed, unkwon reason";
    const data = JSON.stringify(err, Object.getOwnPropertyNames(err));
    return {
      name,
      status,
      duration,
      message,
      data
    };
  }
};
var probeCheck = async (config) => {
  const dependencies = await Promise.all(config.map(dependencyHealthCheck));
  const statuses = dependencies.map((d) => d.status);
  let status = "Ok";
  if (statuses.includes("Error")) {
    status = "Error";
  } else if (statuses.includes("Warning")) {
    status = "Warning";
  }
  return { status, dependencies };
};
var PROBE_CACHE_KEY = "probe";
var PROBE_CACHE_TTL = 1 * 60 * 1e3;
var probe = async (_req, res, dependencies) => {
  try {
    const cachedResult = cache.get(PROBE_CACHE_KEY);
    if (cachedResult) {
      if ((cachedResult == null ? void 0 : cachedResult.status) === "Error") {
        res.status(500).json(cachedResult);
      } else {
        res.status(200).json(cachedResult);
      }
      return;
    }
    const result = await probeCheck(dependencies);
    cache.set({ name: PROBE_CACHE_KEY, data: result, ttl: PROBE_CACHE_TTL });
    if ((result == null ? void 0 : result.status) === "Error") {
      res.status(500).json(result);
    } else {
      res.status(200).json(result);
    }
  } catch (err) {
    res.status(500).json({ message: "Internal Server Error" });
  }
};

// src/smoke.js
var smoke = async (_req, res, dependencies) => {
  try {
    const result = await probeCheck(dependencies);
    if ((result == null ? void 0 : result.status) === "Error") {
      res.status(500).json(result);
    } else {
      res.status(200).json(result);
    }
  } catch (err) {
    res.status(500).json({ message: "Internal Server Error" });
  }
};

// src/valueIn.js
var IMMUTABLE_ITERABLE_FLAG = "@@__IMMUTABLE_ITERABLE__@@";
function isImmutableIterable(potentialImmutable) {
  return Boolean(
    potentialImmutable && potentialImmutable[IMMUTABLE_ITERABLE_FLAG]
  );
}
var DEFAULT_VALUE = void 0;
var PATH_SEPARATOR = ".";
function convertToPathArray(path) {
  if (typeof path === "string") {
    return path.split(PATH_SEPARATOR);
  }
  if (Array.isArray(path)) {
    return path.slice();
  }
  return [];
}
function valueIn(target, path, defaultValue = DEFAULT_VALUE) {
  const pathArray = convertToPathArray(path);
  if (target === void 0 || target === null) {
    return defaultValue;
  }
  if (pathArray.length === 0) {
    return target;
  }
  let currentTarget = target;
  while (pathArray.length) {
    if (currentTarget === void 0 || currentTarget === null) {
      return defaultValue;
    }
    currentTarget = isImmutableIterable(currentTarget) ? currentTarget.get(pathArray.shift()) : currentTarget[pathArray.shift()];
  }
  return currentTarget || defaultValue;
}
valueIn.array = function isPresentInArray(array, key) {
  return Boolean(array && array.includes(key));
};

// src/childrenAreEmpty.js
function isFalsy(value) {
  const type = typeof value;
  if (!value && type !== "number" || type === "string" && !value.length) {
    return true;
  }
  if (value.constructor === Array) {
    return !value.filter((v) => !isFalsy(v)).length;
  }
  return false;
}
var childrenAreEmpty = (children) => {
  return isFalsy(children);
};
export {
  DEFAULT_VALUE,
  baseFetch,
  childrenAreEmpty,
  createCache,
  isImmutableIterable,
  jsonFetch,
  jsonFetchKeepAlive,
  probe,
  probeCheck,
  smoke,
  textFetch,
  tryCatch,
  valueIn
};
